import type { AqItemType, AqPriority } from '@elva/ui';
import type { AlCategory, AlStatus } from '@elva/ui';
import type { ClAlertType, ClHealth, ClLpPhase } from '@elva/ui';
import type { LdStatusKey } from '@elva/ui';
import type { MarketingLeadStatus } from './marketingApiTypes';
import { enumLower } from './adapt';

/** Backend item_type (13 values) → UI ui_bucket (6 tabs). */
const APPROVAL_TYPE_TO_BUCKET: Record<string, AqItemType> = {
  campaign_launch: 'campaign',
  campaign: 'campaign',
  campaign_suggestion: 'campaign',
  ad_variant: 'campaign',
  suggested_keyword_batch: 'campaign',
  article: 'article',
  article_draft: 'article',
  landing_page: 'landing-page',
  landing_page_variant: 'landing-page',
  gbp_post: 'gbp-post',
  gbp_offer_post: 'gbp-post',
  gbp_review_response: 'review-reply',
  review_response: 'review-reply',
  review_reply: 'review-reply',
  seo_fix: 'seo-fix',
  seo_audit_issue: 'seo-fix',
  schema: 'seo-fix',
};

/** Wire ui_bucket (snake_case or short names) → UI tab id. */
const APPROVAL_UI_BUCKET_TO_FE: Record<string, AqItemType> = {
  campaign: 'campaign',
  article: 'article',
  landing: 'landing-page',
  landing_page: 'landing-page',
  'landing-page': 'landing-page',
  gbp_post: 'gbp-post',
  'gbp-post': 'gbp-post',
  review_reply: 'review-reply',
  'review-reply': 'review-reply',
  seo_fix: 'seo-fix',
  'seo-fix': 'seo-fix',
};

export function approvalItemTypeToUiBucket(itemType: string | null | undefined): AqItemType {
  const key = (itemType ?? '').toLowerCase().replace(/-/g, '_');
  return APPROVAL_TYPE_TO_BUCKET[key] ?? 'campaign';
}

export function approvalUiBucketToFe(bucket: string | null | undefined): AqItemType {
  if (!bucket) return 'campaign';
  const key = bucket.toLowerCase().replace(/-/g, '_');
  return APPROVAL_UI_BUCKET_TO_FE[key] ?? approvalItemTypeToUiBucket(bucket);
}

/** Map UI tab id → wire `?ui_bucket=` (underscored short names). */
export function approvalFeTabToBeUiBucket(tab: string | null | undefined): string | undefined {
  if (!tab || tab === 'all') return undefined;
  const map: Record<string, string> = {
    campaign: 'campaign',
    article: 'article',
    'landing-page': 'landing',
    'gbp-post': 'gbp_post',
    'review-reply': 'review_reply',
    'seo-fix': 'seo_fix',
  };
  return map[tab] ?? tab.replace(/-/g, '_');
}

/** Summary/list bucket keys that have no tab — skip when aggregating tab counts. */
export function approvalUiBucketToFeOrNull(bucket: string | null | undefined): AqItemType | null {
  if (!bucket) return null;
  const key = bucket.toLowerCase().replace(/-/g, '_');
  if (key === 'other') return null;
  if (key in APPROVAL_UI_BUCKET_TO_FE) return APPROVAL_UI_BUCKET_TO_FE[key] ?? null;
  if (key in APPROVAL_TYPE_TO_BUCKET) return APPROVAL_TYPE_TO_BUCKET[key] ?? null;
  return null;
}

export function approvalPriorityToFe(value: string | null | undefined): AqPriority {
  const v = (value ?? 'medium').toLowerCase();
  if (v === 'high' || v === 'low' || v === 'medium') return v;
  return 'medium';
}

const LEAD_STATUS_MAP: Record<string, LdStatusKey> = {
  new: 'new',
  contacted: 'contacted',
  qualified: 'contacted',
  booked: 'booked',
  showed: 'showed',
  treated: 'treated',
  no_show: 'noshow',
  noshow: 'noshow',
  lost: 'lost',
  archived: 'lost',
};

export function leadStatusToFe(value: string | null | undefined): LdStatusKey {
  const key = (value ?? 'new').toLowerCase().replace(/-/g, '_');
  return LEAD_STATUS_MAP[key] ?? 'new';
}

/** Live API accepts: new, contacted, qualified, booked, lost, archived. */
export function leadStatusToBe(value: LdStatusKey): MarketingLeadStatus {
  const map: Partial<Record<LdStatusKey, MarketingLeadStatus>> = {
    new: 'new',
    contacted: 'contacted',
    booked: 'booked',
    showed: 'booked',
    treated: 'booked',
    noshow: 'lost',
    lost: 'lost',
  };
  return map[value] ?? 'new';
}

const CALL_SOURCE_MAP: Record<string, string> = {
  google_ads: 'Google Ads',
  ad_click_to_call: 'Ad click-to-call',
  landing_page_ads: 'Landing page (ads)',
  landing_page_sms: 'Landing page (SMS)',
  gbp: 'Google Business Profile',
  organic: 'Organic Search',
  direct: 'Direct',
  referral: 'Referral',
};

export function callSourceToFe(value: string | null | undefined): string {
  if (!value) return '—';
  const key = value.toLowerCase();
  return CALL_SOURCE_MAP[key] ?? value.replace(/_/g, ' ');
}

const AUTOPILOT_CATEGORY_MAP: Record<string, AlCategory> = {
  ads: 'ads',
  google_ads: 'ads',
  campaign: 'ads',
  landing: 'landing',
  landing_page: 'landing',
  seo: 'seo',
  content: 'seo',
  gbp: 'gbp',
  intel: 'intel',
  market: 'intel',
  technical: 'technical',
  call: 'call',
  other: 'technical',
};

export function autopilotCategoryToFe(value: string | null | undefined): AlCategory {
  const key = (value ?? 'other').toLowerCase().replace(/-/g, '_');
  return AUTOPILOT_CATEGORY_MAP[key] ?? 'technical';
}

export function autopilotStatusToFe(value: string | null | undefined): AlStatus {
  const v = (value ?? 'success').toLowerCase();
  if (v === 'retrying' || v === 'failed' || v === 'pending' || v === 'success') return v;
  return 'success';
}

/** Backend autopilot_rules seed keys ↔ Settings UI toggle keys (contract + legacy). */
const AUTOPILOT_RULE_KEY_TO_UI: Record<string, string> = {
  adjust_keyword_bids: 'adjustKeywordBids',
  pause_underperforming_keywords: 'pauseUnderperformingKeywords',
  shift_budget_between_campaigns: 'shiftBudgetBetweenCampaigns',
  ab_test_ad_copy: 'abTestAdCopy',
  launch_new_campaigns: 'launchNewCampaigns',
  increase_budgets: 'increaseBudgets',
  article_drafting: 'articleDrafting',
  review_responses: 'reviewResponses',
  gbp_posts: 'gbpPosts',
  landing_page_variants: 'landingPageVariants',
  campaign_launches: 'campaignLaunches',
  bid_optimization: 'bidOptimization',
};

const AUTOPILOT_RULE_KEY_TO_BE: Record<string, string> = Object.fromEntries(
  Object.entries(AUTOPILOT_RULE_KEY_TO_UI).map(([be, ui]) => [ui, be]),
);

export function autopilotRuleKeyToFe(beKey: string): string {
  return AUTOPILOT_RULE_KEY_TO_UI[beKey] ?? beKey;
}

export function autopilotRuleKeyToBe(uiKey: string): string {
  return AUTOPILOT_RULE_KEY_TO_BE[uiKey] ?? uiKey;
}

export function setupOwnerToFe(value: string | null | undefined): 'you' | 'dentist' | 'elva' {
  const v = (value ?? 'you').toLowerCase();
  if (v === 'dentist' || v === 'elva') return v;
  return 'you';
}

export function setupOwnerToBe(value: 'you' | 'dentist' | 'elva'): string {
  if (value === 'you') return 'dentist';
  return value;
}

export function campaignHealthToFe(value: string | null | undefined): ClHealth {
  const v = (value ?? 'learning').toLowerCase();
  if (v === 'optimal' || v === 'attention' || v === 'paused' || v === 'learning') return v;
  return 'learning';
}

/** Backend alert_type (e.g. quality_drop) → UI success | warning | neutral. */
export function campaignAlertTypeToFe(value: string | null | undefined): ClAlertType {
  const v = (value ?? '').toLowerCase();
  if (!v || v === 'neutral' || v === 'info') return 'neutral';
  if (v === 'success' || v.includes('performing') || v.includes('above')) return 'success';
  return 'warning';
}

export function campaignLpPhaseToFe(value: string | null | undefined): ClLpPhase | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === 'exploration' || v === 'validation' || v === 'exploitation' || v === 'converged') {
    return v;
  }
  return null;
}
